<template>
  <div class="content-grid">
    <Card v-if="member" :title="t('routes.account.name')">
      <form class="form" novalidate @submit.prevent="handleSubmit">
        <FormRow :with-three-columns="true">
          <FormInput
              :ref="addFormInputRef"
              v-model="member.firstName"
              :label="t('global.firstName')"
              name="firstName"
              type="text"
              @validated="handleValidation"
          />
          <FormInput
              :ref="addFormInputRef"
              v-model="member.lastName"
              :label="t('global.lastName')"
              name="lastName"
              type="text"
              @validated="handleValidation"
          />
          <FormInput
              v-if="canEditJobTitle && canHaveAnyJobTitle"
              :ref="addFormInputRef"
              v-model="member.jobTitle"
              :label="t('account.form.jobTitle')"
              name="jobTitle"
              type="text"
              @validated="handleValidation"
          />
          <FormSelect
              v-if="canEditJobTitle && !canHaveAnyJobTitle"
              :ref="addFormInputRef"
              v-model="member.jobTitle"
              :label="t('global.jobTitle')"
              :options="jobTitles"
              name="jobTitle"
              @validated="handleValidation"/>
          <FormReadonlyField
              v-if="!canEditJobTitle"
              v-model="member.jobTitle"
              :label="t('global.jobTitle')"
              name="jobTitle"/>
        </FormRow>
        <FormRow :with-three-columns="true">
          <FormInput
              v-model="member.apartment"
              :label="t('global.apartment')"
              :rules="[]"
              name="apartment"
              type="number"
              @validated="handleValidation"
          />
          <FormInput
              :ref="addFormInputRef"
              v-model="member.street"
              :label="t('global.street')"
              :list="'address-list'"
              name="street"
              type="text"
              @validated="handleValidation"
              @update:modelValue="handleInput">
            <template v-slot:before-input>
              <datalist id="address-list">
                <option v-for="address in addressOptions" :id="address.id" :key="address.id">
                  {{ address.text }}
                </option>
              </datalist>
            </template>
          </FormInput>
          <FormInput
              :ref="addFormInputRef"
              v-model="member.city"
              :label="t('global.city')"
              name="city"
              type="text"
              @validated="handleValidation"
          />
        </FormRow>
        <FormRow :with-three-columns="true">
          <FormInput
              :ref="addFormInputRef"
              v-model="member.zipCode"
              :label="t('global.zipCode')"
              :rules="[required, mustMatchZipCodeFormat]"
              name="zipCode"
              type="zip"
              @validated="handleValidation"
          />
          <div class="form__duo-fields">
            <FormInput
                :ref="addFormInputRef"
                v-model="member.phoneNumber"
                :label="t('global.phoneNumber')"
                :rules="[required, mustMatchPhoneNumberFormat]"
                name="phoneNumber"
                type="tel"
                @validated="handleValidation"
            />
            <FormInput
                :ref="addFormInputRef"
                v-model="member.phoneExtension"
                :label="t('global.phoneExtension')"
                :rules="[]"
                name="phoneExtension"
                type="number"
                @validated="handleValidation"/>
          </div>
        </FormRow>
        <button class="form__submit btn btn--fullscreen">
          {{ t("global.save") }}
        </button>
      </form>
    </Card>

    <MembershipCertificate/>

    <div class="content-grid content-grid--double">
      <ChangePassword/>
      <ChangeEmail/>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, ref} from "vue";
import {useI18n} from "vue3-i18n";
import {useMemberStore} from "@/stores/memberStore";
import {useAddressService, useMemberService} from "@/inversify.config";
import {mustMatchPhoneNumberFormat, mustMatchZipCodeFormat, required,} from "@/validation/rules";
import {notifyError, notifySuccess} from "@/notify";
import {Role} from "@/types";
import {Status} from "@/validation";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import Card from "@/components/layout/Card.vue";
import FormInput from "@/components/forms/FormInput.vue";
import FormRow from "@/components/forms/FormRow.vue";
import ChangePassword from "@/views/ChangePassword.vue";
import ChangeEmail from "@/views/ChangeEmail.vue";
import MembershipCertificate from "@/components/memberships/MembershipCertificate.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import FormReadonlyField from "@/components/forms/FormReadonlyField.vue";
import {IFilterAddressesRequest, IGetAddressRequest} from "@/types/requests";
import {IAddress} from "@/types/responses";

const {t, getLocale} = useI18n()
const memberStore = useMemberStore();
const memberService = useMemberService();
const addressService = useAddressService()

const member = computed(() => memberStore.getMember);
const updateMemberResponse = ref<SucceededOrNotResponse>();
const addressOptions = ref<IAddress[]>([])

const rolesAllowedToEditJobTitle = [Role.CorporateAdmin, Role.RegularSyndicateAdmin, Role.RegularSyndicateManager, Role.RegularIndividual]
const rolesThatCanHaveAnyJobTitle = [Role.CorporateAdmin, Role.RegularIndividual]

const canEditJobTitle = computed(() => memberStore.hasOneOfTheseRoles(rolesAllowedToEditJobTitle))
const canHaveAnyJobTitle = computed(() => memberStore.hasOneOfTheseRoles(rolesThatCanHaveAnyJobTitle))
const jobTitles = [
  {
    label: t('member.jobTitle.administrator.label'),
    name: t('member.jobTitle.administrator.value')
  },
  {
    label: t('member.jobTitle.manager.label'),
    name: t('member.jobTitle.manager.value')
  },
  {
    label: t('member.jobTitle.coowner.label'),
    name: t('member.jobTitle.coowner.value')
  },
]

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses: any = {}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref))
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid;
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some((x) => x === false)) {
    notifyError(t("global.formErrorNotification"));
    return;
  }

  updateMemberResponse.value = (await memberService.updateAuthenticatedMember(
      member.value
  )) as SucceededOrNotResponse;

  updateMemberResponse.value?.succeeded
      ? notifySuccess(t("account.form.successMessage"))
      : notifyError(t("account.form.failedMessage"));
}

async function handleInput(inputValue: string) {
  let selectedOptions = addressOptions.value.filter(x => x.text == inputValue)
  if (selectedOptions.length > 0) {
    let request = {
      id: selectedOptions[0].id,
      language: getLocale()
    } as IGetAddressRequest
    let response = await addressService.getAddress(request)
    member.value.street = response.detailedAddress.street
    member.value.city = response.detailedAddress.city
    member.value.zipCode = response.detailedAddress.zipCode.replace(' ', '')
    return;
  }
  await loadMatchingAddresses(inputValue)
}

async function loadMatchingAddresses(inputValue: string) {
  let request = {
    street: inputValue,
    language: getLocale()
  } as IFilterAddressesRequest
  let addresses = await addressService.filterAddresses(request)
  addressOptions.value = addresses.matchingAddresses
}
</script>
